import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { recordInstallmentPayment } from 'services/loans'
import { faPlain } from 'utilities/helper/format'

/**
 * «ثبت پرداخت» for a basket — record N installments as received by hand.
 *
 * The endpoint settles one installment per call (always the earliest unpaid
 * one), so this walks the basket **sequentially**: two calls in flight would
 * both read the same «earliest unpaid» row and one would be wasted.
 *
 * A failure part-way through is reported with the count that DID land rather
 * than swallowed — money already recorded against the fund cannot be quietly
 * un-said, and the manager needs to know where the run stopped.
 */
export default function useRecordInstallments(
    moneyPool: string | undefined,
    loanId: string | number | undefined
) {
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const [isRecording, setIsRecording] = useState(false)

    /**
     * `tag` is the manager's optional note. It is written onto EVERY
     * installment in the basket — «۳ قسط را نقدی گرفتم #تسویه_نقدی» describes
     * the whole handover, and the rows are indistinguishable afterwards, so
     * putting the note on only one of them would lose it for the other two.
     */
    const record = async (count: number, tag?: string) => {
        if (!moneyPool || !loanId || count < 1 || isRecording) return
        setIsRecording(true)
        const note = tag?.trim() || undefined
        let done = 0
        try {
            for (let i = 0; i < count; i += 1) {
                // eslint-disable-next-line no-await-in-loop
                await recordInstallmentPayment(moneyPool, loanId, note)
                done += 1
            }
            posthog.capture('loan_installments_recorded', {
                moneypool_id: moneyPool,
                loan_id: loanId,
                count: done,
                has_description: !!note,
                has_hashtag: !!note && note.includes('#'),
            })
            toast.success(
                done > 1
                    ? `${faPlain.format(done)} قسط ثبت شد`
                    : 'پرداخت قسط ثبت شد'
            )
        } catch (error: any) {
            posthog.capture('loan_installments_record_failed', {
                moneypool_id: moneyPool,
                loan_id: loanId,
                requested: count,
                recorded: done,
                status: error?.response?.status,
            })
            toast.error(
                done > 0
                    ? `فقط ${faPlain.format(
                          done
                      )} قسط ثبت شد؛ بقیه ثبت نشد. دوباره تلاش کنید.`
                    : 'ثبت پرداخت انجام نشد'
            )
        } finally {
            setIsRecording(false)
            if (done > 0) {
                // The loan page, the fund's balance and the payables list all
                // moved; none of them should be read from cache after this.
                queryClient.invalidateQueries()
                navigate(-1)
            }
        }
    }

    return { record, isRecording }
}
